import FocusTrap from 'focus-trap-react';
import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup';

import EllipsisIcon from '../../../../static/icons/actions/ic_more_ios_24.svg';
import { sourceCardOpened, sourceCardOptionClicked } from '../../../actions/SourceCard';
import Icon from '../../../components/Icon/Icon';
import { AriaRoles, messages as m } from '../../../constants/AccessibilityConstants';
import { SourceCardIcons } from '../../../constants/Icon';
import { KeyCodes } from '../../../constants/Keyboard';
import QATags from '../../../constants/QATags';
import {
  SourceCardActionsMessages,
  SourceCardConstants,
  SourceCardCSSConstants,
  SourceCardSubmenuComponents,
  SourceCardTriggerTypes,
} from '../../../constants/SourceCard';
import { tooltipMessageTypes } from '../../../constants/Tooltip';
import { AsyncDomain } from '../../../domains/index';
import { AppContext } from '../../../lib/Context';
import { attemptComponentEventNotification } from '../../../lib/EventUtils';
import { getIsShareOrStandardModalOpen } from '../../../lib/Overlay';
import { createBEM } from '../../../lib/utils/css';
import {
  addDocumentEventListener,
  addWindowEventListener,
  removeDocumentEventListener,
  removeWindowEventListener,
} from '../../../tools/Globals';
import * as Utils from '../../../utils';
import Tooltip from '../../lib/Tooltip/Tooltip';
import SourceCardMenuItem from '../SourceCardMenuItem/SourceCardMenuItem';
import SourceCardSubMenuItem from '../SourceCardMenuItem/SourceCardSubMenuItem';

import './SourceCard.scss';
import './SourceCard-animation.scss';

export class SourceCard extends React.Component {
  static propTypes = {
    children: PropTypes.any,
    className: PropTypes.string,
    intl: intlShape.isRequired,
    isButtonRow: PropTypes.bool,
    isDark: PropTypes.bool,
    isEllipsis: PropTypes.bool,
    isEllipsisSmall: PropTypes.bool,
    isEllipsisXsmall: PropTypes.bool,
    isShareOrStandardModalOpen: PropTypes.bool,
    isUnavailable: PropTypes.bool,
    onToggleActive: PropTypes.func,
    onTriggerToggleFocus: PropTypes.func,
    qaTag: PropTypes.oneOf(values(QATags)),
    sourceCardMenuItems: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        onClick: PropTypes.func,
        qaTag: PropTypes.oneOf(values(QATags)),
        subSourceCardMenuData: PropTypes.shape({
          type: PropTypes.string,
          pandoraId: PropTypes.string,
          qaTag: PropTypes.oneOf(values(QATags)),
        }),
        subSourceCardMenuItems: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            onClick: PropTypes.func,
            qaTag: PropTypes.oneOf(values(QATags)),
          }),
        ),
      }),
    ),
    sourceCardOpened: PropTypes.func.isRequired,
    sourceCardOptionClicked: PropTypes.func.isRequired,
    tooltipOnEllipsis: PropTypes.bool,
    triggerHidden: PropTypes.bool,
    type: PropTypes.array,
  };

  static defaultProps = {
    type: [SourceCardConstants.CLICK],
  };

  state = {
    currentScrollTop: 0,
    hideOutline: false,
    initialScrollTop: null,
    isEllipsisFocused: false,
    isFocusInSourceCard: true,
    isPortalOpen: false,
    isVisible: false,
    relativeCoords: {},
  };

  componentWillReceiveProps(nextProps) {
    this.focusInSourceCardTimeout = setTimeout(() => {
      if (!nextProps.isShareOrStandardModalOpen) {
        if (!this.unmounting) {
          this.setState({ isFocusInSourceCard: true });
        }
      }
    }, 250); // Modal leave transition timeout is 200
  }

  componentWillUnmount() {
    this.unmounting = true;
    this.hideMenuEventListeners(false);
    if (this.toggleActiveTimeout) {
      clearTimeout(this.toggleActiveTimeout);
      this.toggleActiveTimeout = null;
    }
    this.focusInSourceCardTimeout && clearTimeout(this.focusInSourceCardTimeout);
  }

  static contextType = AppContext;

  hideMenuEventListeners = shouldTurnOn => {
    if (shouldTurnOn) {
      addDocumentEventListener('mousedown', this.hideSourceCardMenu);
      addDocumentEventListener('scroll', this.hideSourceCardMenu);
      addWindowEventListener('resize', this.hideSourceCardMenu);
    } else {
      removeDocumentEventListener('mousedown', this.hideSourceCardMenu);
      removeDocumentEventListener('scroll', this.hideSourceCardMenu);
      removeWindowEventListener('resize', this.hideSourceCardMenu);
    }
  };

  getSourceCardMenuItems = () => {
    return this.props.sourceCardMenuItems.menuItemsGenerator
      ? this.props.sourceCardMenuItems.menuItemsGenerator()
      : this.props.sourceCardMenuItems;
  };

  getIntegrationData = (...additionalContexts) => {
    return this.context.integration.getActionData(
      this.props.sourceCardMenuItems.integrationContext,
      ...additionalContexts,
    );
  };

  hideSourceCardMenuHelper = () => {
    this.hideMenuEventListeners(false);
    if (!this.unmounting) {
      this.setState({
        isVisible: false,
        initialScrollTop: null,
        hideOutline: false,
      });
      if (this.props.onToggleActive) {
        this.toggleActiveTimeout = setTimeout(() => {
          this.props.onToggleActive(false);
          this.setState({
            isPortalOpen: false,
          });
        }, SourceCardCSSConstants.FADETIMEOUT);
      }
    }
  };

  hideSourceCardMenu = e => {
    if (e.type === 'scroll') {
      // Give users a margin of error before autoclosing
      if (!this.state.initialScrollTop) {
        return this.setState({ initialScrollTop: Utils.Scroll.getScrollAmount() });
      } else if (
        Math.abs(Utils.Scroll.getScrollAmount() - this.state.initialScrollTop) <=
        SourceCardConstants.SCROLLTHRESHOLD
      ) {
        return;
      }
    }

    e.preventDefault();
    const sourceCardMenuElement = this.sourceCardMenuElement;
    if (
      e.type === 'resize' ||
      (sourceCardMenuElement && !sourceCardMenuElement.contains(e.target))
    ) {
      this.hideSourceCardMenuHelper();
    }
  };

  showSourceCardMenu = e => {
    e.stopPropagation();
    e.preventDefault();
    this.props.sourceCardOpened(
      this.getIntegrationData({
        componentEventType: SourceCardTriggerTypes.ELLIPSIS_TRIGGER,
      }),
    );
    this.setState({
      isPortalOpen: true,
      isVisible: true,
      relativeCoords: this.getRelativeCoords(e),
      currentScrollTop: Utils.Scroll.getScrollAmount(),
    });
    this.hideMenuEventListeners(true);
    this.props.onToggleActive ? this.props.onToggleActive(true) : null;
  };

  getMenuStyles = sourceCardMenuLength => {
    const menuWidth = SourceCardCSSConstants.MENUWIDTH;
    const menuHeight = this.calculateHeight(sourceCardMenuLength);
    const menuShift = this.calculateShift(menuWidth, menuHeight);
    return this.calculateMenuStyles(menuShift);
  };

  getSubMenuStyles = (subSourceCardMenuLength, parentIndex) => {
    const maxHeight = SourceCardCSSConstants.MAXSUBMENUHEIGHT;
    const menuWidth = SourceCardCSSConstants.MENUWIDTH * 2;
    const menuHeight = this.calculateHeight(subSourceCardMenuLength);
    const maxMenuHeight = menuHeight > maxHeight ? maxHeight : menuHeight;

    const menuShift = this.calculateShift(menuWidth, maxMenuHeight);
    const translateAmount = SourceCardCSSConstants.SUBMENUSHIFT;
    const marginShift = this.calculateMarginShift(maxMenuHeight, parentIndex);
    return this.calculateSubMenuStyles(menuShift, translateAmount, marginShift);
  };

  calculateMenuStyles = menuShift => {
    const unit = SourceCardCSSConstants.UNIT;
    let top = this.state.relativeCoords.y;
    let left = this.state.relativeCoords.x;

    if (menuShift.y > 0) {
      top = this.state.relativeCoords.y - menuShift.y;
    }

    if (menuShift.x > 0) {
      left =
        this.state.relativeCoords.x -
        SourceCardCSSConstants.MENUWIDTH -
        SourceCardCSSConstants.PADDING.x * 2;
    }

    const styles = {
      top: `${top}${unit}`,
      left: `${left}${unit}`,
    };

    return styles;
  };

  calculateSubMenuStyles = (menuShift, translateAmount, marginShift) => {
    let amountToShiftVertically = Math.max(0, marginShift);
    let amountToShiftHorizontally = translateAmount;

    if (
      menuShift.x > 0 &&
      this.state.relativeCoords.x <
        SourceCardCSSConstants.MENUWIDTH * 2 + SourceCardCSSConstants.PADDING.x * 2
    ) {
      amountToShiftHorizontally = SourceCardCSSConstants.OVERLAPSHIFT;
      amountToShiftVertically = marginShift < 10 ? 10 : amountToShiftVertically;
    }

    const styles = {
      transform: menuShift.x > 0 ? `translateX(-${amountToShiftHorizontally})` : null,
      marginTop: `-${amountToShiftVertically}${SourceCardCSSConstants.UNIT}`,
    };
    return styles;
  };

  getRelativeCoords = e => {
    let x;
    let y;
    const bounds = e.target.getBoundingClientRect();
    if (e.type === SourceCardConstants.CONTEXTMENU) {
      x = e.clientX + SourceCardCSSConstants.PADDING.x;
      y = e.clientY + SourceCardCSSConstants.PADDING.y + Utils.Scroll.getScrollAmount();
    } else {
      x = bounds.left + bounds.width;
      y = bounds.top + bounds.height + Utils.Scroll.getScrollAmount();
    }
    return { x, y };
  };

  preventPageScroll = e => {
    const el = e.currentTarget.firstElementChild;
    const prevent = function () {
      e.stopPropagation();
      e.preventDefault();
      return false;
    };

    if (e.deltaY > 0 && e.deltaY > el.scrollHeight - el.offsetHeight - el.scrollTop) {
      // Scrolling down, but this will take us past the bottom.
      el.scrollTop = el.scrollHeight;
      return prevent();
    } else if (e.deltaY < 0 && -e.deltaY > el.scrollTop) {
      // Scrolling up, but this will take us past the top.
      el.scrollTop = 0;
      return prevent();
    }
  };

  calculateHeight = menuItemsLength => {
    const numMenuItems = menuItemsLength > 0 ? menuItemsLength : 0;
    const lineHeight = SourceCardCSSConstants.LINEHEIGHT;
    const paddingY = SourceCardCSSConstants.PADDING.y;
    return lineHeight * numMenuItems + paddingY;
  };

  calculateShift = (width, height) => {
    const relativeCoords = this.state.relativeCoords;
    const totalWidth = relativeCoords.x + width;
    const x = totalWidth - window.innerWidth;
    const scrollAmount = this.state.currentScrollTop;
    const totalHeight = relativeCoords.y + height - scrollAmount;
    const y = totalHeight - window.innerHeight + SourceCardCSSConstants.TUNERBARHEIGHT;
    return { x, y };
  };

  calculateMarginShift = (subMenuHeight, parentIndex) => {
    const parentHeight = this.calculateHeight(this.getSourceCardMenuItems().length);
    let parentShift = this.calculateShift(0, parentHeight);
    parentShift = parentShift.y > 0 ? parentShift.y : 0;
    const subMenuInnerOffset = parentIndex * SourceCardCSSConstants.LINEHEIGHT;
    const subMenuOffset = SourceCardCSSConstants.PADDING.y / 2 + subMenuInnerOffset;
    const relativeY = this.state.relativeCoords.y - parentShift - Utils.Scroll.getScrollAmount();
    const totalHeight = relativeY + subMenuOffset + subMenuHeight;
    return totalHeight - window.innerHeight + SourceCardCSSConstants.TUNERBARHEIGHT;
  };

  defaultOnClick = (callback, e) => {
    e.stopPropagation();
    this.hideSourceCardMenuHelper();
    if (callback) {
      callback();
    }
  };

  onSubmenuItemClick = (callback, addType, e) => {
    this.defaultOnClick(callback, e);
  };

  renderHorizontalRule = bool => {
    if (bool) {
      return <hr className='SourceCard__hr' />;
    }
  };

  renderSubSourceCardMenu = (item, parentIndex) => {
    const className = 'SourceCard__subMenu';
    const itemClassName = className + '__item';

    const subMenuItems = item.subSourceCardMenuItems;
    if (subMenuItems) {
      return (
        <ul
          className={className}
          style={this.getSubMenuStyles(subMenuItems.length, parentIndex)}
          onWheel={this.preventPageScroll}
        >
          {subMenuItems.map((subMenuItem, i) =>
            this.renderSubSourceCardMenuItem(subMenuItem, itemClassName, i),
          )}
        </ul>
      );
    }
    const data = item.subSourceCardMenuData;
    if (data && data.type in SourceCardSubmenuComponents) {
      const { component, domain } = SourceCardSubmenuComponents[data.type];
      return (
        <AsyncDomain
          className={className}
          component={component}
          domain={domain}
          getSubMenuStyles={this.getSubMenuStyles}
          itemClassName={itemClassName}
          itemPandoraId={data.pandoraId}
          parentIndex={parentIndex}
          preventPageScroll={this.preventPageScroll}
          onItemClick={this.onSubmenuItemClick}
          qaTag={data.qaTag}
          getIntegrationData={this.getIntegrationData}
        />
      );
    }
  };

  renderSubSourceCardMenuItem = (menuItem, className, i) => {
    const name = menuItem.name;
    const actionName = SourceCardActionsMessages[name];
    const title = actionName ? actionName.defaultMessage : name;
    const text = actionName ? <FormattedMessage {...actionName} /> : name;
    return (
      <li key={`submenu-${name}${i}`} className={className} role={AriaRoles.PRESENTATION}>
        <SourceCardSubMenuItem
          name={name}
          pandoraId={i}
          menuItemClicked={e => this.defaultOnClick(menuItem.onClick, e)}
          renderHr={name === SourceCardConstants.NEWPLAYLIST}
          qaTag={menuItem.qaTag}
          title={title}
          text={text}
        />
      </li>
    );
  };

  onSourceCardFocus = () => {
    this.setState({ isFocusInSourceCard: true });
  };

  onSourceCardBlur = () => {
    this.setState({ isFocusInSourceCard: false });
  };

  renderSourceCardMenu = () => {
    let menu;
    if (this.state.isVisible) {
      const sourceCardMenuItems = this.getSourceCardMenuItems();
      const focusTrapOptions = {
        escapeDeactivates: true,
        clickOutsideDeactivates: true, // cspell:disable-line
        returnFocusOnDeactivate: true,
        onDeactivate: this.hideSourceCardMenuHelper,
      };
      menu = (
        <FocusTrap
          focusTrapOptions={focusTrapOptions}
          paused={!this.state.isFocusInSourceCard}
          role={AriaRoles.MENU}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <ul
            className='SourceCard__menu'
            style={this.getMenuStyles(sourceCardMenuItems.length)}
            ref={el => {
              // the CSSTransitionGroup will wrap this ul so we need to only set
              // the ref for the element itself
              if (el && el.nodeName) {
                this.sourceCardMenuElement = el;
              }
            }}
            onFocus={this.onSourceCardFocus}
            onBlur={this.onSourceCardBlur}
          >
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <li className='visuallyHidden' role={AriaRoles.PRESENTATION} tabIndex='0'>
              <FormattedMessage {...m.sourceCardTrapScreenReaderText} />
            </li>
            {sourceCardMenuItems.map((item, index) => this.renderSourceCardMenuItems(item, index))}
          </ul>
        </FocusTrap>
      );
    }

    return (
      this.state.isVisible &&
      ReactDOM.createPortal(
        <ReactCSSTransitionGroup
          transitionName='fx-sourceCardFade'
          transitionAppear={true}
          transitionAppearTimeout={100}
          transitionEnter={false}
          transitionLeaveTimeout={SourceCardCSSConstants.FADETIMEOUT}
        >
          {menu}
        </ReactCSSTransitionGroup>,
        window.document.body,
      )
    );
  };

  onMenuItemClick = (e, onClick, itemContext) => {
    const eventData = this.getIntegrationData(itemContext);
    attemptComponentEventNotification(onClick, this.props.sourceCardOptionClicked, eventData)(e);
  };

  renderSourceCardMenuItems = (item, index) => {
    let chevron;
    let subMenu;
    const className = 'SourceCard__menu__item';
    const name = item.name;
    const sourceCardAction = SourceCardActionsMessages[name];
    const glyph = SourceCardIcons[name];

    if ((item.subSourceCardMenuData || item.subSourceCardMenuItems) && !item.isDisabled) {
      // Render the submenu.  The submenu should also have a keytrap on activation.
      // add .SourceCard__menu__item--focus to create hover state.
      chevron = <Icon className={`${className}__chevron`} glyph={SourceCardIcons.chevron} />;
      subMenu = this.renderSubSourceCardMenu(item, index);
    }

    return (
      <li
        className='SourceCard__menu__item__container'
        key={`${name}${index}`}
        role={AriaRoles.PRESENTATION}
      >
        {this.renderHorizontalRule(name === SourceCardConstants.REMOVEFROMPLAYLIST)}
        <SourceCardMenuItem
          className={className}
          glyph={glyph}
          name={item.name}
          sourceCardAction={sourceCardAction}
          onClickItem={(e, itemContext) => {
            this.onMenuItemClick(e, item.onClick, itemContext);
          }}
          hideSourceCardMenuHelper={this.hideSourceCardMenuHelper}
          {...item}
        >
          {chevron}
        </SourceCardMenuItem>
        {subMenu}
      </li>
    );
  };

  accessibleTrigger = () => {
    this.setState({ hideOutline: true });
  };

  accessibleTriggerFocus = () => {
    this.setState({ hideOutline: false });
    this.setState({ isEllipsisFocused: true });
    this.props.onTriggerToggleFocus && this.props.onTriggerToggleFocus(true);
    ReactTooltip.show(this.tooltipRef);
  };

  accessibleTriggerBlur = () => {
    this.setState({ hideOutline: true });
    this.setState({ isEllipsisFocused: false });
    this.props.onTriggerToggleFocus && this.props.onTriggerToggleFocus(false);
    ReactTooltip.hide(this.tooltipRef);
  };

  onKeyDown = e => {
    if (e.keyCode === KeyCodes.TAB) {
      this.setState({ hideOutline: false });
    }
  };

  renderEllipsis = () => {
    const { formatMessage } = this.props.intl;
    const classes = createBEM('SourceCard__ellipsis', {
      clicked: this.state.hideOutline,
      small: this.props.isEllipsisSmall,
      xsmall: this.props.isEllipsisXsmall,
    });
    return (
      <button
        aria-haspopup='true'
        aria-label={formatMessage({ ...SourceCardActionsMessages.ellipsis })}
        className={classes}
        disabled={this.props.isUnavailable}
        onBlur={this.accessibleTriggerBlur}
        onClick={this.accessibleTrigger}
        onFocus={this.accessibleTriggerFocus}
        onKeyDown={this.onKeyDown}
      >
        <Icon glyph={EllipsisIcon} />
      </button>
    );
  };

  renderEllipsisTooltip = () => {
    return (
      <Tooltip
        tipText={
          this.props.isUnavailable ? tooltipMessageTypes.UNAVAILABLE : tooltipMessageTypes.MORE
        }
        style={{ height: '100%', width: '100%', cursor: 'pointer' }}
        offset={{ top: -10 }}
        tooltipRef={el => {
          this.tooltipRef = el;
        }}
      />
    );
  };

  renderTrigger = () => {
    const type = this.props.type;
    const hasOnClick = type.includes(SourceCardConstants.CLICK) && !this.props.isUnavailable;
    const hasOnContextMenu =
      type.includes(SourceCardConstants.CONTEXTMENU) && !this.props.isUnavailable;
    const triggerClasses = createBEM('SourceCard__trigger', {
      hidden: this.props.triggerHidden,
    });
    return (
      <div
        className={triggerClasses}
        data-qa={this.props.qaTag}
        onClick={hasOnClick ? this.showSourceCardMenu : null}
        onContextMenu={hasOnContextMenu ? this.showSourceCardMenu : null}
        role='button'
      >
        {this.props.isEllipsis ? this.renderEllipsis() : this.props.children}
        {this.props.isEllipsis && this.props.tooltipOnEllipsis
          ? this.renderEllipsisTooltip()
          : null}
      </div>
    );
  };

  renderA11yFocusText = () => {
    if (this.props.isButtonRow && this.props.isEllipsis && this.state.isEllipsisFocused) {
      return (
        <div className='SourceCard__a11yText'>
          <FormattedMessage {...m.more} />
        </div>
      );
    }
  };

  render() {
    const classes = createBEM('SourceCard', {
      dark: this.props.isDark,
    });

    return (
      <div className={classes}>
        {this.renderTrigger()}
        {this.renderSourceCardMenu()}
        {this.renderA11yFocusText()}
      </div>
    );
  }
}

export function mapStateToProps(state) {
  return {
    isShareOrStandardModalOpen: getIsShareOrStandardModalOpen(state),
  };
}

const mapDispatchToProps = {
  sourceCardOpened,
  sourceCardOptionClicked,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(SourceCard));
